/**
 * CENTRALIZED LIVE MATCH STORE
 * 
 * Single Source of Truth for all live match data across the application.
 * 
 * CRITICAL ARCHITECTURE DECISION:
 * - All live UI must read from this centralized store
 * - Never independently fetch or manage live state per component
 * - This ensures consistent live scores, minutes, and status everywhere
 * 
 * Usage:
 * - Wrap your app with <LiveMatchStoreProvider>
 * - Use useLiveMatchStore() to access live match data
 * - Use useLiveMatch(matchId) to get a specific match with live updates
 */

import React, { createContext, useContext, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Match } from '@/lib/types';
import { logError } from '@/lib/errorHandling';

// Live data polling intervals
const LIVE_POLL_INTERVAL = 15 * 1000; // 15 seconds when live matches exist
const KICKOFF_WINDOW_POLL_INTERVAL = 30 * 1000; // 30 seconds when kickoffs imminent
const IDLE_CHECK_INTERVAL = 60 * 1000; // 60 seconds when no live matches

interface LiveMatchData {
  id: string;
  home_score: number | null;
  away_score: number | null;
  status: Match['status'];
  current_minute: number | null;
  half_status: string | null;
  extra_minutes: number | null;
  live_stats: Match['statistics']['live_stats'] | null;
  live_events: Match['statistics']['live_events'] | null;
  updated_at: string;
}

interface LiveMatchStoreState {
  // Map of match_id -> live data
  liveMatches: Map<string, LiveMatchData>;
  // All live matches as array
  allLiveMatches: Match[];
  // Count of live matches
  liveCount: number;
  // Whether we're currently polling
  isPolling: boolean;
  // Loading state
  isLoading: boolean;
  // Last update timestamp
  lastUpdateAt: Date | null;
  // Get live data for a specific match (returns undefined if not live)
  getLiveData: (matchId: string) => LiveMatchData | undefined;
  // Check if a match is currently live
  isMatchLive: (matchId: string) => boolean;
  // Force refresh live data
  refresh: () => Promise<void>;
}

const LiveMatchStoreContext = createContext<LiveMatchStoreState | null>(null);

/**
 * Extract live data fields from a match object
 */
function extractLiveData(match: Match): LiveMatchData {
  return {
    id: match.id,
    home_score: match.home_score,
    away_score: match.away_score,
    status: match.status,
    current_minute: match.statistics?.current_minute ?? null,
    half_status: match.statistics?.half_status ?? null,
    extra_minutes: match.statistics?.extra_minutes ?? null,
    live_stats: match.statistics?.live_stats ?? null,
    live_events: match.statistics?.live_events ?? null,
    updated_at: match.updated_at,
  };
}

// Pages that don't need live match data
const PAGES_WITHOUT_LIVE_DATA = [
  '/best-betting-sites',
  '/about',
  '/faq',
  '/data-accuracy',
  '/privacy-policy',
  '/terms-of-service',
];

/**
 * Check if current page needs live match data
 */
function useShouldFetchLiveData(): boolean {
  const [shouldFetch, setShouldFetch] = React.useState(() => {
    // Check initial path
    if (typeof window === 'undefined') return true;
    const path = window.location.pathname;
    return !PAGES_WITHOUT_LIVE_DATA.some(p => path.startsWith(p));
  });

  useEffect(() => {
    // Listen for route changes
    const checkPath = () => {
      const path = window.location.pathname;
      setShouldFetch(!PAGES_WITHOUT_LIVE_DATA.some(p => path.startsWith(p)));
    };

    // Check on mount
    checkPath();

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', checkPath);
    
    return () => {
      window.removeEventListener('popstate', checkPath);
    };
  }, []);

  return shouldFetch;
}

export function LiveMatchStoreProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const hasUpcomingKickoffsRef = useRef(false);
  const lastDataHashRef = useRef<string>('');
  const shouldFetch = useShouldFetchLiveData();

  // Main query for live matches - disabled on pages that don't need it
  const {
    data: liveMatches,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['live-match-store'],
    queryFn: async (): Promise<Match[]> => {
      try {
        // Skip logging in production for performance
        if (process.env.NODE_ENV !== 'production') {
          console.log('[LiveMatchStore] Fetching live matches...');
        }
        
        const { data, error } = await supabase
          .from('matches')
          .select(`*, league:leagues(*)`)
          .eq('status', 'live')
          .order('kickoff_time', { ascending: true });

        if (error) {
          console.error('[LiveMatchStore] Supabase error:', error);
          logError('LiveMatchStore', error, { operation: 'fetch' });
          throw error;
        }

        // Handle null/undefined data gracefully
        const rawMatches = data ?? [];
        
        if (process.env.NODE_ENV !== 'production') {
          console.log(`[LiveMatchStore] Received ${rawMatches.length} live matches`);
        }

        const matches = rawMatches.map((m: any) => ({
          ...m,
          status: m.status as Match['status'],
          statistics: m.statistics as Match['statistics'],
        }));

        // Create hash to detect actual changes
        const dataHash = matches.map(m => {
          const minute = m.statistics?.current_minute ?? 0;
          const halfStatus = m.statistics?.half_status ?? '';
          return `${m.id}:${m.home_score ?? 0}-${m.away_score ?? 0}:${minute}:${halfStatus}`;
        }).join('|');

        // Only log when data actually changes
        if (dataHash !== lastDataHashRef.current && matches.length > 0 && process.env.NODE_ENV !== 'production') {
          console.log(`[LiveMatchStore] Data updated: ${matches.length} live matches`);
          lastDataHashRef.current = dataHash;
        }

        return matches;
      } catch (error) {
        console.error('[LiveMatchStore] Error:', error);
        logError('LiveMatchStore', error, { operation: 'fetch' });
        throw error;
      }
    },
    // Disable fetching on pages that don't need live data
    enabled: shouldFetch,
    staleTime: 8 * 1000, // 8 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: (query) => {
      // Don't poll if disabled
      if (!shouldFetch) return false;
      
      const data = query.state.data;
      const hasLive = data && data.length > 0;

      if (hasLive) {
        return LIVE_POLL_INTERVAL; // 15s when live
      }

      if (hasUpcomingKickoffsRef.current) {
        return KICKOFF_WINDOW_POLL_INTERVAL; // 30s when kickoffs imminent
      }

      return IDLE_CHECK_INTERVAL; // 60s otherwise
    },
    refetchIntervalInBackground: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Check for upcoming kickoffs - only when fetching is enabled
  useEffect(() => {
    if (!shouldFetch) return;
    
    const checkUpcomingKickoffs = async () => {
      try {
        const now = new Date();
        const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000);

        const { data } = await supabase
          .from('matches')
          .select('id')
          .eq('status', 'scheduled')
          .gte('kickoff_time', now.toISOString())
          .lte('kickoff_time', thirtyMinutesFromNow.toISOString())
          .limit(1);

        hasUpcomingKickoffsRef.current = (data?.length ?? 0) > 0;
      } catch {
        // Ignore errors in background check
      }
    };

    checkUpcomingKickoffs();
    const interval = setInterval(checkUpcomingKickoffs, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [shouldFetch]);

  // Real-time subscription for instant updates
  // Only connect when fetching is enabled, defer to avoid blocking first paint
  useEffect(() => {
    if (!shouldFetch) return;
    
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let mounted = true;
    
    // Delay WebSocket connection to avoid blocking first paint
    // Use requestIdleCallback if available, otherwise setTimeout
    const scheduleConnection = () => {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => {
          if (mounted && shouldFetch) connectRealtime();
        }, { timeout: 3000 });
      } else {
        setTimeout(() => {
          if (mounted && shouldFetch) connectRealtime();
        }, 2000);
      }
    };
    
    const connectRealtime = () => {
      try {
        channel = supabase
          .channel('live-match-store-realtime')
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'matches',
            },
            (payload) => {
              if (!mounted) return;
              
              const newData = payload.new as any;
              
              // Optimistically update the cache
              queryClient.setQueryData<Match[]>(['live-match-store'], (oldData) => {
                if (!oldData) return oldData;

                // If match is now live and wasn't before, add it
                if (newData.status === 'live') {
                  const existingIndex = oldData.findIndex(m => m.id === newData.id);
                  if (existingIndex >= 0) {
                    // Update existing
                    const updated = [...oldData];
                    updated[existingIndex] = {
                      ...updated[existingIndex],
                      home_score: newData.home_score,
                      away_score: newData.away_score,
                      status: newData.status,
                      statistics: newData.statistics,
                      updated_at: newData.updated_at,
                    };
                    return updated;
                  }
                  // Will be picked up on next poll
                } else if (newData.status !== 'live') {
                  // Remove from live matches if status changed from live
                  return oldData.filter(m => m.id !== newData.id);
                }

                return oldData;
              });

              // Invalidate to ensure fresh data
              if (newData.status === 'live') {
                queryClient.invalidateQueries({ queryKey: ['live-match-store'] });
              }
            }
          )
          .subscribe((status, err) => {
            // Handle subscription errors silently to avoid console noise
            if (err && process.env.NODE_ENV !== 'production') {
              console.warn('[LiveMatchStore] Realtime subscription error:', err.message);
            }
          });
      } catch (error) {
        // Silently handle WebSocket connection errors
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[LiveMatchStore] Failed to connect realtime:', error);
        }
      }
    };
    
    scheduleConnection();

    return () => {
      mounted = false;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [queryClient, shouldFetch]);

  // Build the live matches map
  const liveMatchesMap = useMemo(() => {
    const map = new Map<string, LiveMatchData>();
    if (liveMatches) {
      liveMatches.forEach(match => {
        map.set(match.id, extractLiveData(match));
      });
    }
    return map;
  }, [liveMatches]);

  // Get live data for a specific match
  const getLiveData = useCallback((matchId: string): LiveMatchData | undefined => {
    return liveMatchesMap.get(matchId);
  }, [liveMatchesMap]);

  // Check if a match is live
  const isMatchLive = useCallback((matchId: string): boolean => {
    return liveMatchesMap.has(matchId);
  }, [liveMatchesMap]);

  // Force refresh
  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo<LiveMatchStoreState>(() => ({
    liveMatches: liveMatchesMap,
    allLiveMatches: liveMatches ?? [],
    liveCount: liveMatches?.length ?? 0,
    isPolling: isFetching,
    isLoading,
    lastUpdateAt: liveMatches && liveMatches.length > 0 ? new Date() : null,
    getLiveData,
    isMatchLive,
    refresh,
  }), [liveMatchesMap, liveMatches, isFetching, isLoading, getLiveData, isMatchLive, refresh]);

  return (
    <LiveMatchStoreContext.Provider value={value}>
      {children}
    </LiveMatchStoreContext.Provider>
  );
}

/**
 * Hook to access the centralized live match store
 */
export function useLiveMatchStore(): LiveMatchStoreState {
  const context = useContext(LiveMatchStoreContext);
  if (!context) {
    throw new Error('useLiveMatchStore must be used within a LiveMatchStoreProvider');
  }
  return context;
}

/**
 * Hook to get live data for a specific match
 * Returns the match with merged live data if available
 * 
 * @param match - The match object to enhance with live data
 * @returns Match with live data merged in (or original if not live)
 */
export function useMatchWithLiveData(match: Match | null): Match | null {
  const { getLiveData, isMatchLive } = useLiveMatchStore();

  return useMemo(() => {
    if (!match) return null;

    // If match is not live in our store, return as-is
    if (!isMatchLive(match.id)) {
      return match;
    }

    // Get live data and merge it
    const liveData = getLiveData(match.id);
    if (!liveData) return match;

    // Merge live data into match object
    return {
      ...match,
      home_score: liveData.home_score,
      away_score: liveData.away_score,
      status: liveData.status,
      statistics: {
        ...match.statistics,
        current_minute: liveData.current_minute ?? undefined,
        half_status: liveData.half_status ?? undefined,
        extra_minutes: liveData.extra_minutes ?? undefined,
        live_stats: liveData.live_stats ?? undefined,
        live_events: liveData.live_events ?? undefined,
      },
      updated_at: liveData.updated_at,
    };
  }, [match, getLiveData, isMatchLive]);
}

/**
 * Hook to get live minute display for a match
 * Returns consistent minute/status across all components
 */
export function useLiveMinute(matchId: string | undefined): {
  minute: number | null;
  halfStatus: string | null;
  displayMinute: string;
  isLive: boolean;
} {
  const { getLiveData, isMatchLive } = useLiveMatchStore();

  return useMemo(() => {
    if (!matchId || !isMatchLive(matchId)) {
      return {
        minute: null,
        halfStatus: null,
        displayMinute: '',
        isLive: false,
      };
    }

    const liveData = getLiveData(matchId);
    if (!liveData) {
      return {
        minute: null,
        halfStatus: null,
        displayMinute: '',
        isLive: false,
      };
    }

    const minute = liveData.current_minute;
    const halfStatus = liveData.half_status;
    
    let displayMinute = '';
    if (minute && minute > 0) {
      displayMinute = `${minute}'`;
      if (halfStatus && halfStatus !== 'LIVE') {
        displayMinute += ` (${halfStatus})`;
      }
    }

    return {
      minute,
      halfStatus,
      displayMinute,
      isLive: true,
    };
  }, [matchId, getLiveData, isMatchLive]);
}

/**
 * Hook to get live score for a match
 * Returns consistent score across all components
 */
export function useLiveScore(matchId: string | undefined): {
  homeScore: number | null;
  awayScore: number | null;
  isLive: boolean;
} {
  const { getLiveData, isMatchLive } = useLiveMatchStore();

  return useMemo(() => {
    if (!matchId || !isMatchLive(matchId)) {
      return {
        homeScore: null,
        awayScore: null,
        isLive: false,
      };
    }

    const liveData = getLiveData(matchId);
    if (!liveData) {
      return {
        homeScore: null,
        awayScore: null,
        isLive: false,
      };
    }

    return {
      homeScore: liveData.home_score,
      awayScore: liveData.away_score,
      isLive: true,
    };
  }, [matchId, getLiveData, isMatchLive]);
}
